import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Session } from '@supabase/supabase-js';
import { supabase, isConfigured, checkAdminStatus } from './services/supabase';
import { processImages } from './services/imageProcessor';
import { trackEvent } from './services/analytics';
import { saveJob, restoreJob, clearJob, hasSavedJob } from './services/jobStorage';
import { AppState } from './types';
import type { UploadedImage, ProcessingOptions, ProcessingProgress, WatermarkOptions, RenameOptions, ResizeOptions } from './types';
import { DEFAULT_WATERMARK_SCALE, DEFAULT_WATERMARK_OPACITY, DEFAULT_WATERMARK_MARGIN, DEFAULT_STANDARD_QUALITY } from './constants';
import Header from './components/Header';
import Footer from './components/Footer';
import FluidBackground from './components/FluidBackground';
import LandingPage from './components/LandingPage';
import BatchEditor from './components/BatchEditor';
import ProcessingView from './components/ProcessingView';
import DownloadView from './components/DownloadView';
import Login from './components/Login';
import AdminStats from './components/AdminStats';

const PENDING_DOWNLOAD_KEY = 'batch_pending_download';

const App: React.FC = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [appState, setAppState] = useState<AppState>(AppState.LANDING);

  const [images, setImages] = useState<UploadedImage[]>([]);
  const [resizeOptions, setResizeOptions] = useState<ResizeOptions>({
    mode: 'standard',
    targetWidth: 1600,
    quality: DEFAULT_STANDARD_QUALITY,
  });
  const [skippedFiles, setSkippedFiles] = useState<{ fileName: string; reason: string }[]>([]);
  const [watermarkOptions, setWatermarkOptions] = useState<WatermarkOptions>({
    enabled: false,
    logoFile: null,
    logoPreviewUrl: null,
    position: 'bottom-right',
    scale: DEFAULT_WATERMARK_SCALE,
    opacity: DEFAULT_WATERMARK_OPACITY,
    margin: DEFAULT_WATERMARK_MARGIN,
  });
  const [renameOptions, setRenameOptions] = useState<RenameOptions>({ enabled: false, pattern: '' });

  const [processingProgress, setProcessingProgress] = useState<ProcessingProgress | null>(null);
  const [zipBlob, setZipBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('signup');
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAdminStats, setShowAdminStats] = useState(false);
  const pendingDownloadRef = useRef(false);
  const restoringJobRef = useRef(false);

  // --- Restore saved job and auto-process ---
  const restoreSavedJob = useCallback(async () => {
    if (restoringJobRef.current) return;
    restoringJobRef.current = true;

    try {
      const job = await restoreJob();
      if (!job || job.files.length === 0) {
        restoringJobRef.current = false;
        localStorage.removeItem(PENDING_DOWNLOAD_KEY);
        return;
      }

      const restored: UploadedImage[] = job.files.map((f) => ({
        id: f.id,
        file: f.file,
        previewUrl: URL.createObjectURL(f.file),
        originalWidth: f.originalWidth,
        originalHeight: f.originalHeight,
      }));

      setImages(restored);
      setResizeOptions(job.resize);
      setWatermarkOptions(job.watermark);
      setRenameOptions(job.rename);
      setAppState(AppState.PROCESSING);

      const options: ProcessingOptions = {
        resize: job.resize,
        watermark: job.watermark,
        rename: job.rename,
      };

      const { zipBlob: blob, skipped } = await processImages(restored, options, (progress) => {
        setProcessingProgress(progress);
      });

      setSkippedFiles(skipped);
      setZipBlob(blob);
      setAppState(AppState.READY);
      localStorage.removeItem(PENDING_DOWNLOAD_KEY);
      await clearJob();

      trackEvent('batch_processed', undefined, {
        imageCount: restored.length,
        restored: true,
        skipped: skipped.length,
        mode: job.resize.mode,
      });
    } catch (err: any) {
      console.error('Failed to restore job:', err);
      setError('We restored your images but processing failed. Please try again.');
      setAppState(AppState.EDITOR);
      localStorage.removeItem(PENDING_DOWNLOAD_KEY);
      restoringJobRef.current = false;
    }
  }, []);

  // Try to restore a pending job if user is authenticated
  const tryRestorePendingJob = useCallback(async (currentSession: Session | null) => {
    if (!currentSession) return;
    if (restoringJobRef.current) return;

    const hasPending = localStorage.getItem(PENDING_DOWNLOAD_KEY);
    if (!hasPending) return;

    const hasJob = await hasSavedJob();
    if (!hasJob) {
      localStorage.removeItem(PENDING_DOWNLOAD_KEY);
      return;
    }

    trackEvent('batch_auth_converted', currentSession.user?.id);
    restoreSavedJob();
  }, [restoreSavedJob]);

  // --- Auth Initialization ---
  useEffect(() => {
    if (!isConfigured) {
      setAuthLoading(false);
      return;
    }

    const isAuthCallback = window.location.pathname === '/auth/callback';
    const hash = window.location.hash;

    // Handle SSO tokens from cross-app navigation
    if (hash.includes('sso_access_token=') && hash.includes('sso_refresh_token=')) {
      const params = new URLSearchParams(hash.replace(/^#\/?/, ''));
      const accessToken = params.get('sso_access_token');
      const refreshToken = params.get('sso_refresh_token');
      if (accessToken && refreshToken) {
        supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken })
          .then(() => {
            window.location.hash = '';
          });
      }
    }

    // Timeout fallback: if code exchange takes too long on callback, redirect home
    if (isAuthCallback) {
      setTimeout(() => {
        if (window.location.pathname === '/auth/callback') {
          window.history.replaceState(null, '', '/');
          setAuthLoading(false);
        }
      }, 8000);
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) {
        if (isAuthCallback) {
          window.history.replaceState(null, '', '/');
        }
        checkAdminStatus(session.user.id).then(setIsAdmin);
        tryRestorePendingJob(session);
        setAuthLoading(false);
      } else if (!isAuthCallback) {
        setAuthLoading(false);
      }
      // If isAuthCallback and no session yet, keep authLoading true —
      // onAuthStateChange will fire once Supabase exchanges the PKCE code
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);

      if (session) {
        if (window.location.pathname === '/auth/callback') {
          window.history.replaceState(null, '', '/');
        }
        setAuthLoading(false);
        checkAdminStatus(session.user.id).then(setIsAdmin);

        if (pendingDownloadRef.current) {
          pendingDownloadRef.current = false;
          triggerDownload();
        }
      } else {
        setIsAdmin(false);
      }

      if (_event === 'SIGNED_IN' && session) {
        tryRestorePendingJob(session);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // --- Download Logic ---
  const triggerDownload = useCallback(() => {
    if (!zipBlob) return;
    const url = URL.createObjectURL(zipBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'batch-images.zip';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    trackEvent('batch_zip_downloaded', session?.user?.id);
  }, [zipBlob, session]);

  const saveJobBeforeAuth = useCallback(async () => {
    try {
      const files = images.map((img) => ({
        id: img.id,
        file: img.file,
        originalWidth: img.originalWidth,
        originalHeight: img.originalHeight,
      }));
      await saveJob(files, resizeOptions, watermarkOptions, renameOptions);
      localStorage.setItem(PENDING_DOWNLOAD_KEY, 'true');
    } catch (err) {
      console.error('Failed to save job:', err);
    }
  }, [images, resizeOptions, watermarkOptions, renameOptions]);

  const handleDownload = useCallback(() => {
    if (session) {
      triggerDownload();
    } else {
      pendingDownloadRef.current = true;
      trackEvent('batch_auth_gate_shown');
      saveJobBeforeAuth();
      setAuthMode('signup');
      setShowLoginModal(true);
    }
  }, [session, triggerDownload, saveJobBeforeAuth]);

  // --- Processing ---
  const handleProcess = useCallback(async () => {
    if (images.length === 0) return;
    setError(null);
    setAppState(AppState.PROCESSING);

    try {
      const options: ProcessingOptions = {
        resize: resizeOptions,
        watermark: watermarkOptions,
        rename: renameOptions,
      };

      const { zipBlob: blob, skipped } = await processImages(images, options, (progress) => {
        setProcessingProgress(progress);
      });

      setSkippedFiles(skipped);

      if (skipped.length === images.length) {
        // Everything was skipped (e.g. all panoramas failed the 2:1 check).
        setError(
          resizeOptions.mode === 'tour360'
            ? 'None of the uploaded images are 2:1 panoramas. 360° mode requires equirectangular images.'
            : 'No files could be processed.'
        );
        setAppState(AppState.EDITOR);
        return;
      }

      setZipBlob(blob);
      setAppState(AppState.READY);
      trackEvent('batch_processed', session?.user?.id, {
        imageCount: images.length,
        skipped: skipped.length,
        mode: resizeOptions.mode,
      });
    } catch (err: any) {
      console.error('Processing failed:', err);
      setError(err.message || 'Processing failed. Please try again.');
      setAppState(AppState.EDITOR);
    }
  }, [images, resizeOptions, watermarkOptions, renameOptions, session]);

  // --- Navigation ---
  const handleStartEditor = useCallback(() => {
    setAppState(AppState.EDITOR);
    trackEvent('batch_landing_view', session?.user?.id);
  }, [session]);

  const handleReset = useCallback(() => {
    setImages([]);
    setZipBlob(null);
    setProcessingProgress(null);
    setError(null);
    setResizeOptions({ mode: 'standard', targetWidth: 1600, quality: DEFAULT_STANDARD_QUALITY });
    setSkippedFiles([]);
    setWatermarkOptions({
      enabled: false,
      logoFile: null,
      logoPreviewUrl: null,
      position: 'bottom-right',
      scale: DEFAULT_WATERMARK_SCALE,
      opacity: DEFAULT_WATERMARK_OPACITY,
      margin: DEFAULT_WATERMARK_MARGIN,
    });
    setRenameOptions({ enabled: false, pattern: '' });
    setAppState(AppState.EDITOR);
  }, []);

  const handleBackToEditor = useCallback(() => {
    setZipBlob(null);
    setProcessingProgress(null);
    setAppState(AppState.EDITOR);
  }, []);

  const handleLogin = () => {
    setAuthMode('login');
    setShowLoginModal(true);
  };

  const handleSignUp = () => {
    setAuthMode('signup');
    setShowLoginModal(true);
  };

  const handleAuthSuccess = () => {
    setShowLoginModal(false);
    trackEvent('batch_auth_converted', session?.user?.id);
  };

  // --- Render ---
  return (
    <div className="min-h-screen flex flex-col bg-black">
      <FluidBackground />
      <Header
        onReset={() => setAppState(AppState.LANDING)}
        session={session}
        onLogin={handleLogin}
        onSignUp={handleSignUp}
        isAdmin={isAdmin}
        onShowStats={() => setShowAdminStats(true)}
      />

      <main className="flex-1 flex flex-col relative z-10">
        {appState === AppState.LANDING && (
          <LandingPage onStart={handleStartEditor} />
        )}

        {appState === AppState.EDITOR && (
          <BatchEditor
            images={images}
            setImages={setImages}
            resizeOptions={resizeOptions}
            setResizeOptions={setResizeOptions}
            watermarkOptions={watermarkOptions}
            setWatermarkOptions={setWatermarkOptions}
            renameOptions={renameOptions}
            setRenameOptions={setRenameOptions}
            onProcess={handleProcess}
            error={error}
            setError={setError}
            userId={session?.user?.id}
          />
        )}

        {appState === AppState.PROCESSING && processingProgress && (
          <ProcessingView progress={processingProgress} />
        )}

        {appState === AppState.READY && (
          <DownloadView
            imageCount={images.length}
            onDownload={handleDownload}
            onStartNew={handleReset}
            onBackToEditor={handleBackToEditor}
            isAuthenticated={!!session}
            skippedFiles={skippedFiles}
          />
        )}
      </main>

      <Footer />

      {showAdminStats && <AdminStats onClose={() => setShowAdminStats(false)} />}

      {showLoginModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <Login
            onClose={() => {
              setShowLoginModal(false);
              pendingDownloadRef.current = false;
            }}
            initialMode={authMode}
            onAuthSuccess={handleAuthSuccess}
            onBeforeRedirect={saveJobBeforeAuth}
          />
        </div>
      )}
    </div>
  );
};

export default App;
